import React, { useEffect, useMemo, useState } from "react";
import { Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Stack, useMediaQuery, useTheme, Tab, Tabs } from "@mui/material";
import {
  GridColDef,
  GridPaginationModel,
  GridRenderCellParams,
} from "@mui/x-data-grid";
import dayjs from "dayjs";
import { createBooleanOperator, Loading, StatusBar, Table, } from "../..";
import { useUserInfo } from "../../../store/User.state";
import {
  useAnnulmentQueriesList,
  useAuthorization,
  useGetMerchantStatusList,
  useUpdateAnnulmentQueryViewStatus,
  useAnnulmentQueriesListMutation
} from "../../../hooks";
import { useSetSnackBar } from '../../../store/Snackbar.state';
import { downloadExcel } from "../../../util/downloadExcel";
import { mapReactMUIToFilterOperationTypes, mapReactFilterValueTypeToRequestFilterValueType, RequestFilterValueTypeEnums } from '../../../common/enums/RequestFilterEnums';

interface QueryOptions {
  field?: string;
  value?: any;
  [key: string]: any;
}

interface RequestPayload {
  size: number;
  page: number;
  orderBy: string;
  orderByDesc: boolean;
  merchantId: string | null;
  [key: string]: any;
}

interface PagingResponse<T> {
  result: T;
  totalItems: number;
  pageSize: number;
  page: number;
}

function RenderStatus(props: GridRenderCellParams<any, string>) {
  const { value } = props;
  return <>{value && <StatusBar status={value} />}</>;
}

const OPERATION_TYPE_EQUAL = 1;

export const AnnulmentQueriesListTable = () => {
  const [queryOptions, setQueryOptions] = React.useState<QueryOptions>({});
  const [tableData, setTableData] = useState<PagingResponse<Array<any>>>();
  const [userInfo] = useUserInfo();
  const theme = useTheme();
  const { showDelete, showUpdate } = useAuthorization();
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));
  const [paginationModel, setPaginationModel] = React.useState({
    page: 0,
    pageSize: 25,
  });
  const [requestPayload, setRequestPayload] = useState<RequestPayload>({
    size: paginationModel.pageSize,
    page: paginationModel.page, // Backend 1-based pagination kullanıyorsa
    orderBy: "CreateDate",
    orderByDesc: true,
    merchantId: null,
  });
  const {
    data: annulmentQueriesListResponse,
    mutate: annulmentQueriesListMutate,
    isLoading: annulmentQueriesListLoading,
    isSuccess: annulmentQueriesListSuccess
  } = useAnnulmentQueriesListMutation();
  const setSnackbar = useSetSnackBar();
  const { data: rawMerchantStatus, isLoading: isMerchantStatusLoading } =
    useGetMerchantStatusList({});
  const { mutateAsync: updateViewStatus, isLoading: updateViewStatusLoading } = useUpdateAnnulmentQueryViewStatus();
  const [dynamicFilters, setDynamicFilter] = useState<any[]>([]);
  const [blokedRow, setBlockedRow] = useState<any>({});
  const [tabValue, setTabValue] = useState(0); // 0: Onay Bekleyen, 1: Onaylanan

  useEffect(() => {
    if (annulmentQueriesListResponse?.data) {
      setTableData({
        result: annulmentQueriesListResponse.data.result || [],
        totalItems: annulmentQueriesListResponse.data.totalItems || 0,
        pageSize: requestPayload.size,
        page: requestPayload.page - 1, // MUI DataGrid 0-based pagination kullanıyor
      });
    }
  }, [annulmentQueriesListResponse, requestPayload]);

  const merchantStatuses = useMemo(() => {
    return rawMerchantStatus?.data?.map(
      (merchantState: { value: string; key: number }) => {
        return {
          label: merchantState.key,
          value: `${merchantState.value}`,
        };
      }
    );
  }, [rawMerchantStatus?.data]);

  useEffect(() => {
    const savedFilter = localStorage.getItem("annulmentQueriesFilter");
    if (savedFilter) {
      setQueryOptions(JSON.parse(savedFilter));
    }
  }, []);

  const handleChangePagination = (model: GridPaginationModel) => {
    setPaginationModel({ page: model.page, pageSize: model.pageSize });
  };

  const [openModal, setOpenModal] = useState(null);

  const getTabViewStatusFilter = () => [
    {
      field: "viewStatus",
      values: [tabValue === 0 ? "false" : "true"],
      valueType: RequestFilterValueTypeEnums.BOOLEAN,
      operationType: OPERATION_TYPE_EQUAL
    }
  ];

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  };

  const handleViewStatusUpdate = async (params: any) => {
    try {
      setBlockedRow(params);
      await updateViewStatus({ id: params.id, viewStatus: !params.viewStatus });
      const newPayload = {
        size: paginationModel.pageSize,
        page: paginationModel.page,
        orderBy: "CreateDate",
        orderByDesc: true,
        merchantId: userInfo?.merchantId !== "0" ? String(userInfo?.merchantId) ?? null : null,
        filters: [
          ...getTabViewStatusFilter(),
          ...dynamicFilters
        ]
      };
      annulmentQueriesListMutate(newPayload);
      setOpenModal(null);
    } catch (error) {
      console.error("View status update failed:", error);
    }
  };

  const columns: GridColDef[] = useMemo(() => {
    return [
    {
      field: "id",
      headerName: "ID",
      width: 120,
      filterable: false,
      sortable: false
    },
    {
      field: "viewStatus",
      headerName: "Kontrol Durumu",
      width: 200,
      columnType: RequestFilterValueTypeEnums.BOOLEAN,
      filterOperators: [createBooleanOperator([
        {
          label: 'Kontrol Edildi', value: true // string yerine boolean
        },
        {
          label: 'Kontrol Edilmedi', value: "false" // string yerine boolean
        },
      ])],
      renderCell: (params: any) => {
        return (
          <>
            <Button
              variant={params.row.viewStatus ? "outlined" : "contained"}
              size="small"
              onClick={() => setOpenModal(params.row)}
              disabled={params.row.viewStatus || blokedRow?.id === params.row.id}
            >
              {params.row.viewStatus ? "Kontrol Edildi" : "Kontrol Edilmedi"}
            </Button >
          </>
        );
      },
    },
    {
      renderCell: RenderStatus,
      field: "merchantStatus",
      headerName: "İşyeri Durumu",
      width: 130,
      filterOperators: [createBooleanOperator(merchantStatuses)],
      columnType: RequestFilterValueTypeEnums.STRING
    },
    {
      field: "pspNo",
      headerName: "Bildiren Üye Numarası",
      width: 150,
      columnType: RequestFilterValueTypeEnums.STRING
    },
    {
      field: "globalMerchantId",
      headerName: "Global İşyeri No",
      width: 150,
      columnType: RequestFilterValueTypeEnums.STRING
    },
    {
      field: "merchantId",
      headerName: "Üye No",
      width: 120,
      columnType: RequestFilterValueTypeEnums.NUMBER
    },
    {
      field: "merchantName",
      headerName: "İşyeri Adı",
      width: 200,
      columnType: RequestFilterValueTypeEnums.STRING
    },
    {
      field: "tradeName",
      headerName: "İşyeri Ticari Adı",
      width: 200,
      columnType: RequestFilterValueTypeEnums.STRING
    },
    {
      field: "managerName",
      headerName: "Sahip / Yönetici Adı",
      width: 180,
      columnType: RequestFilterValueTypeEnums.STRING
    },
    {
      field: "taxNo",
      headerName: "TCK/Vergi No",
      width: 130,
      columnType: RequestFilterValueTypeEnums.STRING
    },
    {
      field: "annulmentCode",
      headerName: "Fesih Kodu",
      width: 180,
      columnType: RequestFilterValueTypeEnums.STRING
    },
    {
      field: "annulmentDate",
      headerName: "Fesih Tarihi",
      width: 180,
      type: 'date',
      valueGetter: (params) => params.value ? new Date(params.value) : null,
      columnType: RequestFilterValueTypeEnums.DATE,
      renderCell: (params) => {
        if (!params.value) return "";
        const formattedDate = dayjs(params.value).format("DD.MM.YYYY");
        if (formattedDate === "Invalid Date") return "";
        return formattedDate;
      },
    },
    {
      field: "annulmentExplanation",
      headerName: "Açıklama",
      width: 200,
      columnType: RequestFilterValueTypeEnums.STRING
    },
    {
      field: "annulmentOwnerIdentityNo",
      headerName: "İşy. Sah. 1. Ortağın TCK/Vergi No",
      width: 180,
      columnType: RequestFilterValueTypeEnums.STRING
    },

    {
      field: "annulmentPartner2IdentityNo",
      headerName: "2. Ortağın TCK/Vergi No",
      width: 150,
      columnType: RequestFilterValueTypeEnums.STRING
    },
    {
      field: "city",
      headerName: "Şehir",
      width: 150,
      columnType: RequestFilterValueTypeEnums.STRING
    },
    {
      field: "address",
      headerName: "Adres",
      width: 300,
      columnType: RequestFilterValueTypeEnums.STRING
    },
    {
      field: "district",
      headerName: "İlçe",
      width: 150,
      columnType: RequestFilterValueTypeEnums.STRING
    },
    {
      field: "mcc",
      headerName: "MCC",
      width: 100,
      columnType: RequestFilterValueTypeEnums.STRING
    },
    {
      field: "chargebackWindowStatus",
      headerName: "Chargeback Penceresi",
      columnType: RequestFilterValueTypeEnums.BOOLEAN,
      renderCell: (props: any) => {
        const { value } = props;
        return value ? "Evet" : "Hayır";
      },
      width: 150,
      filterOperators: [createBooleanOperator([{
        label: 'Evet', value: true // string yerine boolean
      },
      {
        label: 'Hayır', value: "false" // string yerine boolean
      }])]
    },
    {
      field: "chargebackWindowOpenDate",
      headerName: "Chargeback Penceresi Açılış Tarihi",
      width: 150,
      type: 'date',
      valueGetter: (params) => params.value ? new Date(params.value) : null,
      columnType: RequestFilterValueTypeEnums.DATE
    },
    {
      field: "chargebackWindowCloseDate",
      headerName: "Chargeback Penceresi Kapanış Tarihi",
      width: 150,
      type: 'date',
      valueGetter: (params) => params.value ? new Date(params.value) : null,
      columnType: RequestFilterValueTypeEnums.DATE
    },
    {
      field: "fictitiousStatus",
      headerName: "Fiktif",
      columnType: RequestFilterValueTypeEnums.BOOLEAN,
      renderCell: (props: any) => {
        const { value } = props;
        return value ? "Evet" : "Hayır";
      },
      width: 150,
      filterOperators: [createBooleanOperator([{
        label: 'Evet', value: true // string yerine boolean
      },
      {
        label: 'Hayır', value: "false" // string yerine boolean
      }])]
    },
    {
      field: "fictitiousCode",
      headerName: "Fiktif Kodu",
      width: 120,
      columnType: RequestFilterValueTypeEnums.STRING
    },
    {
      field: "fictitiousOpenDate",
      headerName: "Fiktif Açılış Tarihi",
      width: 120,
      type: 'date',
      valueGetter: (params) => params.value ? new Date(params.value) : null,
      renderCell: (params) => {
        if (!params.value) return "";
        const formattedDate = dayjs(params.value).format("DD.MM.YYYY HH:mm");
        if (formattedDate === "Invalid Date") return "";
        return formattedDate;
      },
      columnType: RequestFilterValueTypeEnums.DATE
    },
    {
      field: "fictitiousCloseDate",
      headerName: "Fiktif Kapanış Tarihi",
      width: 120,
      type: 'date',
      valueGetter: (params) => params.value ? new Date(params.value) : null,
      renderCell: (params) => {
        if (!params.value) return "";
        const formattedDate = dayjs(params.value).format("DD.MM.YYYY HH:mm");
        if (formattedDate === "Invalid Date") return "";
        return formattedDate;
      },
      columnType: RequestFilterValueTypeEnums.DATE
    },
    {
      field: "annulmentInformType",
      headerName: "Fesih Tipi",
      width: 120,
      columnType: RequestFilterValueTypeEnums.STRING
    },
    {
      field: "annulmentCreateDate",
      columnType: RequestFilterValueTypeEnums.DATE,
      headerName: "Bildirim Tarihi",
      width: 180,
      type: 'date',
      valueGetter: (params) => params.value ? new Date(params.value) : null,
      renderCell: (params) => {
        if (!params.value) return "";
        const formattedDate = dayjs(params.value).format("DD.MM.YYYY HH:mm");
        if (formattedDate === "Invalid Date") return "";
        return formattedDate;
      },
    },
    {
      field: "annulmentPartner3IdentityNo",
      headerName: "3. Ortağın TCK/Vergi No",
      width: 150,
      columnType: RequestFilterValueTypeEnums.STRING
    },
    {
      field: "annulmentPartner4IdentityNo",
      headerName: "4. Ortağın TCK/Vergi No",
      width: 150,
      columnType: RequestFilterValueTypeEnums.STRING
    },
    {
      field: "annulmentPartner5IdentityNo",
      headerName: "5. Ortağın TCK/Vergi No",
      width: 150,
      columnType: RequestFilterValueTypeEnums.STRING
    },
    {
      field: "annulmentPersonnelName",
      headerName: "Feshe Sebep Olan Personel",
      width: 180,
      columnType: RequestFilterValueTypeEnums.STRING
    },
    {
      field: "lastStatusUpdateDate",
      headerName: "İşyeri Durum Son Güncelleme Tarihi",
      width: 180,
      renderCell: (params) => {
        if (!params.value) return "";
        const formattedDate = dayjs(params.value).format("DD.MM.YYYY HH:mm");
        if (formattedDate === "Invalid Date") return "";
        return formattedDate;
      },
      type: 'date',
      valueGetter: (params) => params.value ? new Date(params.value) : null,
      columnType: RequestFilterValueTypeEnums.DATE
    },
    {
      field: "annulmentUpdateDate",
      headerName: "Fesih Son Güncelleme Tarihi",
      width: 180,
      renderCell: (params) => {
        if (!params.value) return "";
        const formattedDate = dayjs(params.value).format("DD.MM.YYYY HH:mm");
        if (formattedDate === "Invalid Date") return "";
        return formattedDate;
      },
      type: 'date',
      valueGetter: (params) => params.value ? new Date(params.value) : null,
      columnType: RequestFilterValueTypeEnums.DATE
    },

    ];
  }, [showDelete, showUpdate, openModal]);

  const onSave = async () => {
    try {
      let excelPayload = {
        size: tableData?.totalItems ?? 99,
        page: 0,
        orderBy: "CreateDate",
        orderByDesc: true,
        merchantId: userInfo?.merchantId !== "0" ? String(userInfo?.merchantId) ?? null : null,
        filters: [
          ...getTabViewStatusFilter(), // <-- Tab filtresi eklendi!
          ...dynamicFilters
        ] // Handle dynamic filters
      };
      await annulmentQueriesListMutate(excelPayload, {
        onSuccess: (response) => {
          // Doğru path: response.data.result önceki versiyonda resultData = response?.data?.data?.result path'i vardı bunu değiştirdim çünkü result direkt data'nın altında. 2 kez data yazmaya gerek yok
          const resultData = response?.data?.result ?? [];
          if (response?.data && resultData?.length > 0) {
            const mappedData = resultData.map((item: any) => {
              return {
                "ID": item?.id,
                "Kontrol Durumu": item?.viewStatus ? "Kontrol Edildi" : "Kontrol Edilmedi",
                "İşyeri Durumu": item?.merchantStatus == "ACTIVE" ? 'Aktif' : item?.merchantStatus == "BLOCKED" ? 'Bloke' : 'Pasif',
                "Bildiren Üye Numarası": item?.pspNo,
                "Global İşyeri No": item?.globalMerchantId,
                "Üye No": item?.merchantId,
                "İşyeri Adı": item?.merchantName,
                "İşyeri Ticari Adı": item?.tradeName,
                "Sahip / Yönetici Adı": item?.managerName,
                "TCK/Vergi No": item?.taxNo,
                "Fesih Kodu": item?.annulmentCode,
                "Fesih Tarihi": item?.annulmentDate ? dayjs(item.annulmentDate).format("DD.MM.YYYY HH:mm") : "",
                "Açıklama": item?.annulmentExplanation,
                "İşy. Sah. 1. Ortağın TCK/Vergi No": item?.annulmentOwnerIdentityNo,
                "2. Ortağın TCK/Vergi No": item?.annulmentPartner2IdentityNo,
                "Şehir": item?.city,
                "Adres": item?.address,
                "İlçe": item?.district,
                "MCC": item?.mcc,
                "Chargeback Penceresi": item?.chargebackWindowStatus ? "Evet" : "Hayır",
                "Chargeback Penceresi Açılış Tarihi": item?.chargebackWindowOpenDate,
                "Chargeback Penceresi Kapanış Tarihi": item?.chargebackWindowCloseDate,
                "Fiktif": item?.fictitiousStatus ? "Evet" : "Hayır",
                "Fiktif Kodu": item?.fictitiousCode,
                "Fiktif Açılış Tarihi": item?.fictitiousOpenDate ? dayjs(item.fictitiousOpenDate).format("DD.MM.YYYY HH:mm") : "",
                "Fiktif Kapanış Tarihi": item?.fictitiousCloseDate ? dayjs(item.fictitiousCloseDate).format("DD.MM.YYYY HH:mm") : "",
                "Fesih Tipi": item?.annulmentInformType,
                "Bildirim Tarihi": item?.AnnulmentCreateDate ? dayjs(item.AnnulmentCreateDate).format("DD.MM.YYYY HH:mm") : "",
                "3. Ortağın TCK/Vergi No": item?.annulmentPartner3IdentityNo,
                "4. Ortağın TCK/Vergi No": item?.annulmentPartner4IdentityNo,
                "5. Ortağın TCK/Vergi No": item?.annulmentPartner5IdentityNo,
                "Feshe Sebep Olan Personel": item?.annulmentPersonnelName,
                "İşyeri Durum Son Güncelleme Tarihi": item?.lastStatusUpdateDate ? dayjs(item.lastStatusUpdateDate).format("DD.MM.YYYY HH:mm") : "",
                "Fesih Son Güncelleme Tarihi": item?.annulmentUpdateDate ? dayjs(item.annulmentUpdateDate).format("DD.MM.YYYY HH:mm") : "",
              };
            });

            if (mappedData.length > 0) {
              downloadExcel(mappedData, "Fesih Takip Sistemi Listesi");
            } else {
              setSnackbar({
                severity: "warning",
                description: "İndirilecek veri bulunamadı",
                isOpen: true,
              });
            }
          } else {
            setSnackbar({
              severity: "error",
              description: "Dışa aktarma işlemi başarısız oldu",
              isOpen: true,
            });
          }
        },
        onError: (error) => {
          setSnackbar({
            severity: "error",
            description: "Dışa aktarma işlemi başarısız oldu",
            isOpen: true,
          });
        }
      });
    } catch (error) {
      setSnackbar({
        severity: "error",
        description: "İşlem sırasında bir hata oluştu",
        isOpen: true,
      });
    }
  };

  const debounce = (func: (...args: any[]) => void, delay: number) => {
    let timeoutId: NodeJS.Timeout;
    return (...args: unknown[]) => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        func.apply(null, args);
      }, delay);
    };
  };

  const getQueryParams = (filters: any) => {
    const queryParams: any = {};

    for (let key in filters) {
      if (filters[key]?.toString()?.length >= 1) {
        queryParams[key] = filters[key];
      }
    }

    return queryParams;
  };

  interface FilterParams {
    clearFilter?: boolean;
    [key: string]: any;
  }
  const previousQueryParams = React.useRef({});

  const convertTableFiltersToRequestFilter = (tableFilters: any) => {

    console.log('table filters', tableFilters);
    let convertedFilters: any = [];

    tableFilters.items.map((item: any) => {

      var findColumn: any = columns.find(x => x.field == item.field);

      convertedFilters.push({
        field: item.field,
        values: [item.value.toString()],
        valueType: mapReactFilterValueTypeToRequestFilterValueType(item.value, findColumn?.columnType),
        operationType: mapReactMUIToFilterOperationTypes(item.operator)
      });
    });

    return convertedFilters;
  }
  const handleFilterChange = debounce((filters, tableFilters) => {
    if (filters.clearFilter) {
      setDynamicFilter([]);
      localStorage.removeItem('paymentAndTranactionFilter')
      return setQueryOptions({});
    }

    const queryParams = getQueryParams(filters);

    const isValueValid = (key: any, value: any) => {
      if (key === 'id' || key === 'installmentCount' || key === 'cardType' || key === 'amount' || key === 'totalAmount' || key === 'totalComission') { // Özel alan kontrolü
        return value?.toString().length >= 1;
      } else {
        return value?.toString().length >= 3;
      }
    }


    const allValuesValid = Object.entries(queryParams).every(([key, value]) => isValueValid(key, value));

    const previousKeysLength = Object.keys(previousQueryParams.current).length;
    const currentKeysLength = Object.keys(queryParams).length;

    // Eğer queryParams bir önceki ile aynıysa işlem yapma
    if (JSON.stringify(previousQueryParams.current) === JSON.stringify(queryParams)) {
      return;
    }

    if (allValuesValid || (previousKeysLength > currentKeysLength)) {
      setQueryOptions(queryParams);
      setDynamicFilter(convertTableFiltersToRequestFilter(tableFilters));
      localStorage.setItem('paymentAndTranactionFilter', JSON.stringify(queryParams));
    }

    previousQueryParams.current = queryParams;

  }, 1000);
  // Ana useEffect - tüm değişiklikleri buradan yönetiyoruz
  useEffect(() => {
    const newPayload = {
      size: paginationModel.pageSize,
      page: paginationModel.page,
      orderBy: "CreateDate",
      orderByDesc: true,
      merchantId: userInfo?.merchantId !== "0" ? String(userInfo?.merchantId) ?? null : null,
      filters: [
        ...getTabViewStatusFilter(),
        ...dynamicFilters
      ] // Handle dynamic filters
    };
    annulmentQueriesListMutate(newPayload);
  }, [paginationModel, queryOptions, userInfo?.merchantId, dynamicFilters, tabValue]);

  const hasActiveFilters = Object?.keys(queryOptions)?.length > 0;

  return (
    <>
      {annulmentQueriesListLoading && <Loading />}
      <Stack flex={1} p={2}>
        <Dialog
          open={!!openModal && Object.keys(openModal).length > 0}
          onClose={() => setOpenModal(null)}
          slotProps={{
            backdrop: {
              style: {
                backgroundColor: 'rgba(0, 0, 0, 0.3)', // Şeffaf arka plan rengi
              },
            },
          }}
        >
          <DialogTitle>İşlemi Onaylayın</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Bu kaydı görüldü olarak işaretlemek istediğinize emin misiniz?
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenModal(null)}>İptal</Button>
            <Button disabled={updateViewStatusLoading} onClick={() => handleViewStatusUpdate(openModal)} autoFocus>
              Onayla
            </Button>
          </DialogActions>
        </Dialog>
        <>
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            sx={{ mb: 2 }}
            centered
          >
            <Tab label="Onay Bekleyen" />
            <Tab label="Onaylanan" />
          </Tabs>
          <Table
            columnVisibilityConfigKey="vposBankListingTableColumnVisibilityKey"
            filterKey="annulmentQueriesFilter"
            hasActiveFilters={hasActiveFilters}
            handleFilterChange={handleFilterChange}
            paginationModel={paginationModel}
            onPaginationModelChange={handleChangePagination}
            paginationMode="server"
            rowCount={tableData?.totalItems ?? 0}
            sx={{ width: isDesktop ? 1308 : window.innerWidth - 50 }}
            isRowSelectable={() => false}
            // disableColumnMenu
            rows={tableData?.result ?? []}
            columns={columns}
            exportFileName="Fesih Takip Sistemi Listesi"
            getRowClassName={(params: any) => {
              if (params?.row?.status === "PASSIVE") return "pasive-row";
              if (params?.row?.status === "BLOCKED") return "blocked-row";
              return "";
            }}
            onSave={() => onSave()}
          />
        </>
      </Stack>
    </>
  );
};

